/* ============================================================
   splash-gate.js — M-25 behaviour: dismissal, entry, hover media.

   Loaded by sections/splash-gate.liquid only, and only when the section has
   at least one door — so it never reaches a page without a gate on it. It is
   deliberately small: the theme's per-page JS budget is 16KB brotli (comments
   stripped) and the homepage rows are the tightest ones. Run node
   tools/audit.mjs after any change here.

   THE CONTRACT: this file may only ever REMOVE the gate early, START an
   animation, SWAP a picture, or CLOSE the gate. Everything the visitor needs —
   the doors, the links, the skip — is in the markup and works with this file
   absent, blocked, or still in flight. There is no loading state and nothing
   waits on the network. That is what makes a dropped connection a non-event.

   THE ONE EXCEPTION IS THE CLICK DIM (2026-08-07). dim() is the only place
   this file stands between a visitor and a link. The setTimeout navigates
   whether or not animationend fires; keep it. A door that swallows its own
   click is the worst bug here. Reasoning: docs/03 §M-25.

   A REAL GATE AS OF 2026-08-04, VIA CSS RATHER THAN THIS FILE. Until then the
   section rendered in flow at 100svh — a screen you scrolled past. It is now
   fixed over a locked page, switched by the `data-gate` attribute that the
   section's inline pre-paint script adds. The CSS does nothing without it, so a
   scriptless visitor keeps the scroll-past screen rather than a panel they
   could not dismiss — same inverse default as `data-ready` below. Extending
   NmpOverlay is what supplies the focus trap, scroll lock and focus restore;
   Escape and scrim-click are overridden away, because a gate a stray keypress
   dismisses is a splash screen again. Full reasoning in docs/03 §M-25.

   The once-per-session check is NOT in this file. It is inline in the section,
   because it has to run before paint or a returning visitor sees the gate
   flash and disappear.

   THE ALWAYS-ON DOOR PICTURES ARE NOT IN THIS FILE (2026-08-04). "Door images
   -> Always visible" is markup and CSS end to end — URL, veil, dim, drift —
   and bindHover() returns early on it. The other two modes stay because they
   genuinely fetch a picture on first hover; that one has nothing to fetch.

   A custom element rather than a bare IIFE, because the theme editor replaces
   the whole section on save. A script tag re-inserted that way never executes
   again, so an IIFE would leave the freshly-rendered gate inert until a full
   reload; connectedCallback re-runs by itself and picks up the new settings.

   Last updated: 2026-09-06
   ============================================================ */
(function () {
  'use strict';

  var DESKTOP = '(min-width: 1024px)';

  /* THE BACKGROUND VIDEO IS NOT IN THIS FILE (2026-08-09) — it is in
     assets/splash-gate-video.js, which carries its own reasoning and the ⚠
     temporary state of its pre-conditions. Split under the per-file cap; the
     section's comment above the two script tags says why and in what order. */

  /* No nmp.js: undo what the pre-paint script did and leave the scroll-past
     screen. ⚠ It returned without undoing it until 2026-08-09, which left a
     fixed gate over a locked page with nothing able to dismiss it. Lock first,
     then data-gate. Full account in the section's comment. */
  var Base = window.NmpOverlay;
  if (!Base) {
    document.documentElement.removeAttribute('data-nmp-locked');
    var stranded = document.querySelectorAll('[data-splash-gate][data-gate]');
    Array.prototype.forEach.call(stranded, function (el) {
      el.removeAttribute('data-gate');
    });
    return;
  }

  class NmpSplashGate extends Base {
    /* ⚠ _gateReady, NOT _ready. NmpOverlay.connectedCallback() sets this._ready
       on the same instance, so calling super first and then testing _ready
       returns immediately and skips everything below — no click handler, no
       open(), no scroll lock, a gate that is merely a fixed picture. It fails
       silently and only in a browser; a caught regression, not a hypothetical. */
    connectedCallback() {
      super.connectedCallback();
      if (this._gateReady) return;
      this._gateReady = true;

      this.editor = this.hasAttribute('data-editor');
      this.isDesktop = window.matchMedia(DESKTOP).matches;
      this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      this.calm = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      /* ---- 1. remove it when it does not apply ------------------------- */
      if (this._retire()) return;

      /* ⚠ The breakpoint moves under us — rotate a tablet, drag a window — and
         until 2026-08-09 nothing re-ran the test above, leaving a hidden gate
         holding the page unscrollable and keyboard-dead. Full account in the
         section's comment above the script tags. */
      this._mq = window.matchMedia(DESKTOP);
      this._onBreakpoint = function (e) {
        this.isDesktop = e.matches;
        this._retire();
      }.bind(this);
      this._mq.addEventListener('change', this._onBreakpoint);

      this._onClick = this.handleClick.bind(this);
      this.addEventListener('click', this._onClick);

      /* The page is already pinned and locked by CSS at this point. open() is
         what hands that state to NmpOverlay — focus trap on, lock reference
         counted, close() able to undo it. Never set in the theme editor. */
      if (this.hasAttribute('data-gate')) this.open();

      this.startEntry();
      /* Guarded, not assumed: assets/splash-gate-video.js is a separate file
         and a blocked one must cost the gate nothing. */
      if (window.NmpSplashVideo) window.NmpSplashVideo(this);
      this.bindHover();
    }

    /* Takes the gate out at a width it does not apply to; reports whether it
       did. Removing the node is what un-reaches the hidden markup for the
       keyboard and, through disconnectedCallback, releases the lock. Safe to
       run twice and before open(). */
    _retire() {
      if (this.editor) return false;
      var on = this.getAttribute('data-visible-on');
      if (!((on === 'desktop' && !this.isDesktop) || (on === 'mobile' && this.isDesktop))) {
        return false;
      }
      /* close() first for focus restore; _teardown releases the lock either
         way. No-ops if never opened. */
      this.close();
      /* The pre-paint script sets data-nmp-locked directly, so if it and the
         test above ever disagree the attribute outlives every reference. */
      if (this.hasAttribute('data-gate')) {
        document.documentElement.removeAttribute('data-nmp-locked');
      }
      this.remove();
      return true;
    }

    /* Not dismissible by Escape or by the backdrop: the only ways past are a
       door and the skip control, both of which record the dismissal. The Tab
       half of NmpOverlay's key handling is still wanted, hence skipping the
       Escape branch rather than the whole method. */
    _onKeydown(event) {
      if (event.key === 'Escape') return;
      super._onKeydown(event);
    }

    _onPointerDown() {}

    /* The editor's re-render builds a whole new element with new doors, so
       this exists for the case where the SAME node is moved rather than
       replaced — reordering a section in Customize does that, and without it
       the doors would collect a second set of listeners on every move. */
    disconnectedCallback() {
      super.disconnectedCallback();
      if (this._mq) {
        this._mq.removeEventListener('change', this._onBreakpoint);
        this._mq = null;
      }
      if (this._onClick) this.removeEventListener('click', this._onClick);
      if (this._onLeave) {
        this.removeEventListener('mouseleave', this._onLeave);
        this.removeEventListener('focusout', this._onFocusOut);
      }
      if (this._doorBindings) {
        this._doorBindings.forEach(function (b) {
          b.door.removeEventListener('mouseenter', b.on);
          b.door.removeEventListener('focus', b.on);
        });
        this._doorBindings = null;
      }
      this._gateReady = false;
      this._dimming = false;
    }

    /* ---- 2. record that it was seen, and dismiss ------------------------ */
    remember() {
      if (this.editor) return;
      var freq = this.getAttribute('data-frequency');
      if (freq === 'always') return;
      try {
        (freq === 'days' ? localStorage : sessionStorage).setItem(
          this.getAttribute('data-storage-key'),
          String(Date.now())
        );
      } catch (e) {
        /* Private mode. The gate simply shows again next time, which is the
           right way for this to fail. */
      }
    }

    handleClick(e) {
      var hit = e.target.closest('[data-splash-door], [data-splash-skip]');
      if (!hit) return;
      /* New-tab click: this tab keeps the gate — header item 7. */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      this.remember();
      /* A control pointing at the page the visitor is already on dismisses in
         place: following it would reload the homepage purely to show it without
         the gate. Covers the skip button AND a door left without a link, whose
         href defaults to '/' — before 2026-08-04 only the skip took this branch
         and an unlinked door reloaded the page. */
      if (hit.getAttribute('href') === window.location.pathname) {
        e.preventDefault();
        this._dismiss();
        return;
      }
      /* Skip is excluded on purpose: it dismisses in place rather than
         navigating, and "get me past this" has to feel instant. */
      if (hit.hasAttribute('data-splash-door')) this.dim(e, hit);
    }

    /* ---- 6. the click dim -----------------------------------------------
       Sets one attribute; the CSS owns the fade. Every reason to decline is
       read BEFORE preventDefault, so a declined click is one the browser has
       already handled normally. */
    dim(e, door) {
      if (this._dimming || this.editor) return;
      if (!this.isDesktop || this.getAttribute('data-dim') !== 'on') return;
      /* A modified or middle click asks for a new tab. Intercepting it would
         fade this window and open nothing. */
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
      var href = door.getAttribute('href');
      if (!href) return;

      e.preventDefault();
      this._dimming = true;

      /* Two ways out, whichever lands first. The timer is not a nicety:
         animationend never fires if the stylesheet failed to load or the
         animation was dropped, and the visitor asked to go somewhere. */
      var gone = false;
      var self = this;
      function go() {
        if (gone) return;
        gone = true;
        self.removeEventListener('animationend', go);
        window.location.href = href;
        /* Still here after 4s: undim — header item 7. */
        setTimeout(function () {
          self._dimming = false;
          self.removeAttribute('data-dimming');
        }, 4000);
      }
      /* The listener is on the element the animation runs on — every layer
         fades, so this fires once per layer and go() de-duplicates. */
      this.addEventListener('animationend', go);
      setTimeout(go, (parseInt(this.getAttribute('data-dim-ms'), 10) || 400) + 400);

      /* Same reason startEntry() uses it: the first painted frame must be the
         "before" state, or the animation starts halfway through. */
      requestAnimationFrame(function () { self.setAttribute('data-dimming', ''); });
    }

    /* close() releases the lock reference, restores focus and pops the stack.
       Dropping data-gate unpins the CSS, and has to happen rather than being
       left to `hidden` alone — the CSS pin is keyed off data-gate, not
       visibility. */
    _dismiss() {
      /* Focus lands on the page content, not <body> — header item 7. */
      this.close({ restoreFocus: false });
      this.removeAttribute('data-gate');
      this.setAttribute('hidden', '');
      var main = document.getElementById('MainContent');
      if (main) {
        main.setAttribute('tabindex', '-1');
        main.focus({ preventScroll: true });
      }
      document.dispatchEvent(new CustomEvent('nmp:splash:dismissed'));
    }

    /* ---- 3. entry -------------------------------------------------------
       One attribute. The CSS does the rest, and does nothing at all without
       it, so a failure to reach this line leaves the finished page rather than
       an invisible one. requestAnimationFrame so the first frame is the
       "before" state rather than a half-applied one. */
    startEntry() {
      if (!this.isDesktop || this.getAttribute('data-entry') === 'none') return;
      var self = this;
      requestAnimationFrame(function () {
        self.setAttribute('data-ready', '');
      });
    }

    /* ---- 5. hover -------------------------------------------------------
       Two genuinely different treatments, and which one runs is decided by the
       Liquid so the CSS and this file cannot disagree:

         plate     (frame 2a) — the hovered door's picture takes over the WHOLE
                                background, on its own layer above the plate
                                and below the scrim.
         accordion (frame 2b) — the door widens (CSS) and lifts its OWN media
                                layer with a slow Ken Burns.

       Dimming is an inline opacity in both, because a sibling-aware selector
       would have to be :has() and this has to work where :has() does not.

       Set on enter, cleared once when the pointer or focus leaves the whole
       gate rather than on every door's own leave. Per-door clearing makes the
       full-screen plate flash off and back on as the pointer crosses a
       divider; the accordion's own layer is cleared explicitly instead. */
    bindHover() {
      var mode = this.getAttribute('data-hover');
      if (!this.isDesktop || !this.finePointer || mode === 'none') return;

      this.doors = Array.prototype.slice.call(this.querySelectorAll('[data-splash-door]'));
      if (!this.doors.length) return;

      /* "Door images -> Always visible" is all CSS: pictures in the markup,
         dim via :hover/:focus-within, drift via transform. Nothing to fetch,
         so nothing to script — and a JS version measured 0.4KB brotli against
         a page budget with 0.8KB left. Look for a stylesheet answer first. */
      if (this.getAttribute('data-door-images') === 'always') return;

      this.plate = this.querySelector('[data-splash-plate]');
      this.accordion = mode === 'accordion';

      var self = this;
      this._onLeave = function () { self.clearHover(); };
      this._onFocusOut = function (e) { if (!self.contains(e.relatedTarget)) self.clearHover(); };
      this.addEventListener('mouseleave', this._onLeave);
      this.addEventListener('focusout', this._onFocusOut);

      this._doorBindings = this.doors.map(function (door) {
        var on = function () { self.activate(door); };
        door.addEventListener('mouseenter', on);
        /* Keyboard parity: tabbing to a door lights it exactly as pointing at
           it does. This is the only reason a keyboard user gets the same
           signal as a mouse user — do not drop it. */
        door.addEventListener('focus', on);
        return { door: door, on: on };
      });
    }

    activate(door) {
      /* Plate has nothing to show over running footage — a still swapped over
         it reads as the video having died — so it must not dim either. The dim
         ran ahead of this test until 2026-08-04, which gave an owner with an
         .mp4 a hover that only ever greyed the row out. Order is the fix.

         Read off the DOM, not off a property splash-gate-video.js wrote: since
         the 2026-08-09 split those are two files, and a shared JS property is
         what rots when one of them is edited alone. It also fails the right
         way — with the video module absent there is no [data-playing], so the
         plate takes over, which is what should happen when nothing is playing. */
      if (!this.accordion && (!this.plate || this.querySelector('[data-splash-video][data-playing]'))) {
        return;
      }

      this.doors.forEach(function (d) { d.style.opacity = d === door ? '1' : '0.42'; });

      var src = door.getAttribute('data-hover-media');
      if (!src) return;

      if (this.accordion) {
        var media = door.querySelector('.m25__door-media');
        if (!media) return;
        if (!media.style.backgroundImage) media.style.backgroundImage = "url('" + src + "')";
        media.style.opacity = '0.55';
        if (!this.calm) media.style.transform = 'scale(1.07)';
        this._lit = media;
        return;
      }

      this.plate.style.backgroundImage = "url('" + src + "')";
      this.plate.style.opacity = '1';
      if (!this.calm) this.plate.style.transform = 'scale(1.05)';
    }

    clearHover() {
      this.doors.forEach(function (d) { d.style.opacity = ''; });
      if (this._lit) {
        this._lit.style.opacity = '0';
        this._lit.style.transform = '';
        this._lit = null;
      }
      if (this.plate) {
        this.plate.style.opacity = '0';
        this.plate.style.transform = '';
      }
    }
  }

  if (!window.customElements.get('nmp-splash-gate')) {
    window.customElements.define('nmp-splash-gate', NmpSplashGate);
  }
})();
